import {
  IonApp,
  IonContent,
  IonHeader,
  IonPage,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
} from '@ionic/react';
import {IonReactRouter} from '@ionic/react-router';
import React, {ElementType, Fragment, ReactElement, useContext, useEffect, useRef} from 'react';
import {Redirect, Route, Switch} from 'react-router-dom';
import Icon from 'shared/components/icon';
import browserHistory from 'shared/history';
import 'src/apps/mobile/ionic.scss';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import {Store} from 'redux';
import {Provider} from 'react-redux';
import ApplicationRoot from '../ApplicationRoot';
import Suspense from 'src/components/Suspense';
import {get, uniqueId} from 'lodash';
import Notifications from '../../../shared/components/notifications';
import {buildNotificationBundlesEvent} from '../../../shared/notification-center/actions/notification-bundle';
import pageView from '../../../shared/page-view';
import styles from './styles.module.scss';
import bottomNavbarStyles from '../../apps/mobile/components/bottom-navbar.module.scss';
import {BottomNavbarProps} from '../../apps/mobile/components/BottomNavbar';
import {useBoolean} from '../../hooks/useBoolean';
import {trackPage} from '../../utils/analytics';
import NotificationAlert from '../notification-alert';
import ImpostorToolbar from '../../../shared/components/impostor-toolbar';
import ToolbarNotification from '../toolbar-notification';
import SimpleMobilePageNavbar from '../mobile-navbar/simple-mobile-page-navbar';
import {ImpostorModeContext} from '../ImpostorModeContext';
import {UserType} from '../../types/enums';
import {accountSettingsTabs} from '../AccountSettings/utils/utils';
import MorePage from '../AccountSettings/more';
import {RaiseEventListenerContext} from '../raise-event-listener-context';
import AwardNotifications from '../../../shared/components/award-notifications';

const logPageView = (pathname: string): void => {
  pageView(pathname);
  trackPage(pathname);
};

export interface MobileRouteConfig {
  path: string | string[];
  component: ElementType;
  navbar?: ReactElement;
  exact?: boolean;
  props?: Record<string, unknown>;
  color?: 'primary' | 'light';
}

export interface MobileRedirectConfig {
  path?: string;
  to: string;
  exact?: boolean;
  as: 'redirect';
}

export interface MorePageConfig {
  awardAmount?: number;
  portal: UserType;
  hideBell?: boolean;
}

export interface MobileApplicationLoaderProps {
  store: Store;
}

type Props = MobileApplicationLoaderProps & {
  bottomNavbarConfig?: BottomNavbarProps;
  modalRoutes?: ReactElement;
  navbar?: ReactElement;
  routes: (MobileRouteConfig | MobileRedirectConfig)[];
  animated?: boolean;
  disableToolbarNotification?: boolean;
  morePage?: MorePageConfig;
  hasAwardsNotifications?: boolean;
};

const ContextMenu = (): ReactElement => (
  <Icon className={bottomNavbarStyles.tabIconPlus} iconType="plus-circle" />
);

const EmptyNavbar = () => {
  const impostorModeContext = useContext(ImpostorModeContext);

  if (impostorModeContext.isImpostor) {
    return null;
  }

  return <SimpleMobilePageNavbar title="" />;
};

export default function MobileApplication({
  bottomNavbarConfig,
  modalRoutes,
  navbar,
  routes,
  store,
  animated,
  disableToolbarNotification,
  morePage,
  hasAwardsNotifications = false,
}: Props) {
  const {push} = browserHistory;
  const {notificationCenterAlert} = useContext(RaiseEventListenerContext);
  const contentRef = useRef<HTMLIonContentElement>(null);
  const contextMenuState = useBoolean(false);

  useEffect(() => {
    buildNotificationBundlesEvent()();

    // Trigger on first landing
    logPageView(location.pathname);

    // trigger loading the tabs which will be memoized so that going to the more page will be fast
    if (morePage?.portal) {
      accountSettingsTabs(morePage.portal);
    }

    return browserHistory.listen(({pathname, state}) => {
      logPageView(pathname);

      if (!get(state, 'skipScrolling', false)) {
        contentRef.current?.scrollToTop();
      }
    });
  }, []);

  const RoutesWrapper = bottomNavbarConfig ? IonTabs : Fragment;

  const renderedRoutes = [...routes];
  if (morePage) {
    renderedRoutes.unshift({
      path: '/more',
      component: MorePage,
      props: {portal: morePage.portal, awardAmount: morePage.awardAmount},
      navbar: (
        <SimpleMobilePageNavbar
          title="More"
          button={
            morePage.hideBell
              ? undefined
              : {
                  onClick: () => {
                    push('/notification-center');
                  },
                  icon: 'bell',
                  showAlert: notificationCenterAlert,
                }
          }
        />
      ),
    });
  }

  return (
    <IonApp>
      {/* eslint-disable-next-line spaced-comment */}
      {/*// @ts-expect-error*/}
      <IonReactRouter history={browserHistory}>
        <Provider store={store}>
          {hasAwardsNotifications && <AwardNotifications />}
          <Notifications />
          <ApplicationRoot>
            {modalRoutes}
            <RoutesWrapper>
              <IonRouterOutlet animated={animated !== undefined ? animated : true}>
                <Switch>
                  {renderedRoutes.map((routeConfig) => {
                    if ('as' in routeConfig && routeConfig.as === 'redirect') {
                      return (
                        <Redirect
                          key={routeConfig.to}
                          path={routeConfig.path}
                          to={routeConfig.to}
                        />
                      );
                    }

                    return (
                      <Route
                        exact={routeConfig.exact}
                        key={
                          typeof routeConfig.path === 'string'
                            ? routeConfig.path
                            : routeConfig.path?.[0]
                        }
                        path={routeConfig.path}
                        render={(routeProps) =>
                          'component' in routeConfig ? (
                            <Suspense>
                              <IonPage>
                                <IonHeader>
                                  <ImpostorToolbar />
                                  {routeConfig.navbar || navbar || <EmptyNavbar />}
                                  {!disableToolbarNotification && <ToolbarNotification />}
                                </IonHeader>
                                <IonContent
                                  ref={contentRef}
                                  scrollEvents
                                  fullscreen
                                  color={routeConfig.color}
                                >
                                  <div className={styles.ionicContentWrapper}>
                                    <routeConfig.component
                                      {...routeProps}
                                      {...(routeConfig.props || {})}
                                    />
                                  </div>
                                </IonContent>
                              </IonPage>
                            </Suspense>
                          ) : null
                        }
                      />
                    );
                  })}
                </Switch>
              </IonRouterOutlet>
              {bottomNavbarConfig && (
                <IonTabBar slot="bottom" className={bottomNavbarStyles.tabsWrapper}>
                  {(bottomNavbarConfig.tabs || []).map((tab, index) => {
                    if (index === bottomNavbarConfig.contextMenuIndex) {
                      return (
                        <IonTabButton
                          className={bottomNavbarStyles.tabButton}
                          href="#"
                          key={uniqueId('ion-bottom-nav-bar-menu')}
                        >
                          {bottomNavbarConfig?.ContextMenu && (
                            <div
                              onClick={(e) => {
                                e.preventDefault();

                                contextMenuState.setTrue();
                              }}
                              className={bottomNavbarStyles.tabIconPlusWrapper}
                            >
                              <ContextMenu />
                            </div>
                          )}
                        </IonTabButton>
                      );
                    }

                    return (
                      <IonTabButton
                        className={bottomNavbarStyles.tabButton}
                        key={tab.key}
                        tab={tab.key}
                        href={`/${tab.key}`}
                      >
                        <NotificationAlert show={tab.showAlert}>
                          {tab.iconName && (
                            <Icon className={bottomNavbarStyles.icon} iconType={tab.iconName} />
                          )}
                        </NotificationAlert>
                        <div>{tab.name}</div>
                      </IonTabButton>
                    );
                  })}
                </IonTabBar>
              )}
            </RoutesWrapper>
            {bottomNavbarConfig?.ContextMenu && (
              <bottomNavbarConfig.ContextMenu
                show={contextMenuState}
                header={bottomNavbarConfig.menuHeader}
                drawerItems={bottomNavbarConfig.drawerItems}
              />
            )}
          </ApplicationRoot>
        </Provider>
      </IonReactRouter>
    </IonApp>
  );
}
